// The colored bubble keeps its ordinary size inside the existing layout envelope.
export const ROUTINE_BUBBLE_SCALE = 1.6;

// Compact inline icon; full node outlines below use their actual dimensions.
const START_DEGREES = -45;
const HEAD_BASE_DEGREES = 10;
const HEAD_BASE = HEAD_BASE_DEGREES * Math.PI / 180;

export function routineOrbitMarkup() {
  const start = START_DEGREES * Math.PI / 180;
  const startX = 50 + 47 * Math.cos(start);
  const startY = 50 + 47 * Math.sin(start);
  const baseX = 50 + 47 * Math.cos(HEAD_BASE);
  const baseY = 50 + 47 * Math.sin(HEAD_BASE);
  // Both arc endpoints lie on that same circle. Avoid dash arrays here:
  // non-scaling strokes can shift their gap when stretched into an ellipse.
  return `<svg class="routine-orbit-ring" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true"><path d="M${startX} ${startY} A47 47 0 1 0 ${baseX} ${baseY}" vector-effect="non-scaling-stroke" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/><path d="M97 50 L${baseX - 5.5} ${baseY} L${baseX + 5.5} ${baseY} Z" fill="currentColor"/></svg>`;
}

// A constant gap keeps the loop close even around very large or wide nodes.
// The 10px-wide head leaves 4px between its inner edge and the node border.
export const ROUTINE_ORBIT_GAP = 9;
const nodeOrbits = new Map();

function nodeOrbit(width, height, radius) {
  const key = `${width}:${height}:${radius}`;
  if (nodeOrbits.has(key)) return nodeOrbits.get(key);
  const rx = width / 2 + ROUTINE_ORBIT_GAP;
  const ry = height / 2 + ROUTINE_ORBIT_GAP;
  const r = Math.min(radius, width / 2, height / 2) + ROUTINE_ORBIT_GAP;
  const diagonal = r * Math.SQRT1_2;
  // Follow the actual rounded outline, counterclockwise, ending at the right
  // midpoint. Its tangent is vertical for both circles and rounded rectangles.
  const shaft = `M${rx-r+diagonal} ${-ry+r-diagonal}
    A${r} ${r} 0 0 0 ${rx-r} ${-ry}
    H${-rx+r} A${r} ${r} 0 0 0 ${-rx} ${-ry+r}
    V${ry-r} A${r} ${r} 0 0 0 ${-rx+r} ${ry}
    H${rx-r} A${r} ${r} 0 0 0 ${rx} ${ry-r} V0`;
  const head = `M${rx} -7 L${rx-5} 5 L${rx+5} 5 Z`;
  const orbit = {rx, ry, shaft, head};
  if (nodeOrbits.size >= 256) nodeOrbits.clear();
  nodeOrbits.set(key, orbit);
  return orbit;
}

/** Size SVG in node coordinates so neither the outline nor head is stretched. */
export function fitRoutineNodeOrbit(badge, width, height, radius) {
  if (!badge || width <= 0 || height <= 0) return;
  const {rx, ry, shaft, head} = nodeOrbit(width, height, radius);
  badge.style.width = `${rx * 2}px`;
  badge.style.height = `${ry * 2}px`;
  badge.innerHTML = `<svg class="routine-orbit-ring" viewBox="${-rx} ${-ry} ${rx*2} ${ry*2}" aria-hidden="true"><path d="${shaft}" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"/><path class="routine-orbit-head" d="${head}" fill="currentColor"/></svg>`;
}

/** Same unscaled geometry as desktop; apply only the view's uniform zoom. */
export function drawRoutineOrbit(ctx, x, y, width, height, radius, color, zoom = 1) {
  if (width <= 0 || height <= 0 || zoom <= 0) return;
  const orbit = nodeOrbit(width, height, radius);
  orbit.canvasShaft ??= new Path2D(orbit.shaft);
  orbit.canvasHead ??= new Path2D(orbit.head);
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(zoom, zoom);
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = Math.max(1 / zoom, 3);
  ctx.lineCap = 'round';
  ctx.stroke(orbit.canvasShaft);
  ctx.fill(orbit.canvasHead);
  ctx.restore();
}
